/** SUNTRONIX ONE — CRM Customer POs. Internal-only. */
import { orgLabel } from "@/lib/orgLabel";
import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Receipt, Plus, RefreshCw, ChevronLeft, CheckCircle2, TrendingUp,
  Upload, ShieldCheck, AlertTriangle, FileText, Truck,
} from "lucide-react";

const fmtINR = (n) => n == null ? "—" :
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR",
    maximumFractionDigits: 0 }).format(n);

export default function CustomerPOList() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const openId = searchParams.get("open");
    if (openId) {
      setDetailId(openId);
      searchParams.delete("open");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/vtiger/customer-pos");
      setRows(data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load POs");
    } finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-2.5 max-w-[1500px]" data-testid="crm-cpo-page">
      <div className="flex items-center justify-between">
        <div>
          <Link to="/commercial/crm" className="text-[11px] text-emerald-700 hover:underline flex items-center gap-1">
            <ChevronLeft className="h-3 w-3" /> Commercial Workspace
          </Link>
          <h1 className="text-xl font-heading font-semibold text-slate-900 mt-0.5 flex items-center gap-2">
            <Receipt className="h-5 w-5 text-emerald-700" />
            Order Confirmations
          </h1>
          <div className="text-[11px] text-slate-500">A formal PO is only one type of customer acceptance — record the true source of confirmation.</div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} disabled={busy}>
            <RefreshCw className={"h-4 w-4 mr-1.5 " + (busy ? "animate-spin" : "")} /> Refresh
          </Button>
          <Button onClick={() => setShowNew(true)}
            className="bg-emerald-700 hover:bg-emerald-800 text-white"
            data-testid="cpo-new">
            <Plus className="h-4 w-4 mr-1.5" /> Record Order Confirmation
          </Button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md divide-y">
        {rows.length === 0 && (
          <div className="p-10 text-center text-[13px] text-slate-500">No order confirmations recorded yet.</div>
        )}
        {rows.map((r) => (
          <button key={r.id} onClick={() => setDetailId(r.id)}
            data-testid={`cpo-row-${r.id}`}
            className="w-full text-left p-4 flex items-center gap-4 hover:bg-slate-50">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-mono text-[13px] font-semibold">{r.code}</span>
                <Badge className="text-[10px] border-emerald-200 bg-emerald-50 text-emerald-700">{r.status}</Badge>
              </div>
              <div className="text-[12px] text-slate-700 mt-1">
                <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-700" data-testid={`cpo-type-${r.id}`}>{r.confirmation_type_label || "Formal Purchase Order"}</span>
                {r.customer_po_number ? <> · PO <span className="font-mono">{r.customer_po_number}</span></> : <> · Ref <span className="font-mono">{r.confirmation_reference || "—"}</span></>}
                {r.quotation_code ? <span> · {r.quotation_code}</span> : r.quotation_id && (<span> · Linked to quotation</span>)}
                {r.auto_created && <span className="ml-1 text-[10px] text-emerald-700">auto</span>}
              </div>
              <div className="text-[10px] text-slate-500 mt-0.5">
                {(r.po_date || r.confirmed_at || r.created_at) ? new Date(r.po_date || r.confirmed_at || r.created_at).toLocaleDateString("en-IN") : "—"} · {r.confirmed_by || r.payment_terms || "—"}
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase text-slate-500">Amount</div>
              <div className="text-[15px] font-heading font-semibold">{fmtINR(r.total_amount_inr)}</div>
            </div>
          </button>
        ))}
      </div>

      {detailId && (
        <CPODetailDrawer id={detailId} onClose={() => setDetailId(null)} />
      )}

      {showNew && (
        <NewPODrawer onClose={() => setShowNew(false)}
          onCreated={() => { setShowNew(false); load(); }} />
      )}
    </div>
  );
}


const TYPES = [["FORMAL_PO", "Formal Purchase Order"], ["TELEPHONIC", "Telephonic Confirmation"], ["VERBAL", "Verbal Confirmation"], ["WHATSAPP", "WhatsApp Confirmation"], ["EMAIL", "Email Confirmation"], ["DIGITAL_QUOTE", "Digital Quote / QR Acceptance"], ["CUSTOMER_PORTAL", "Customer Portal Confirmation"], ["WRITTEN", "Written Confirmation / Letter"], ["REPEAT_ORDER", "Repeat Order Confirmation"], ["INTERNAL_EXCEPTION", "Internal Approved Exception"], ["OTHER", "Other"]];
const AUTO_TYPES = ["DIGITAL_QUOTE", "CUSTOMER_PORTAL"];

function NewPODrawer({ onClose, onCreated }) {
  const [customerMc, setCustomerMc] = useState("");
  const [customers, setCustomers] = useState([]);
  const [quotations, setQuotations] = useState([]);
  const [quotationId, setQuotationId] = useState("");
  const [type, setType] = useState("FORMAL_PO");
  const [f, setF] = useState({ poNum: "", poDate: new Date().toISOString().slice(0, 10), reference: "", confirmedAt: new Date().toISOString().slice(0, 16), confirmedBy: "", messageId: "", explanation: "", amount: "", terms: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const quote = quotations.find((q) => q.id === quotationId);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const inp = "w-full h-9 border border-slate-300 rounded-md text-[13px] px-2";

  useEffect(() => {
    api.get("/master-companies").then(({ data }) => setCustomers(data.items || []));
    api.get("/vtiger/quotations?status=SENT").then(({ data }) => setQuotations(data.items || []));
  }, []);
  useEffect(() => { if (quote?.customer_master_company_id || quote?.customer_account_id) setCustomerMc(quote.customer_master_company_id || quote.customer_account_id); }, [quote]);

  const formal = type === "FORMAL_PO";
  const submit = async () => {
    if (!customerMc) return toast.error("Select the customer");
    if (formal && (!f.poNum || !f.poDate)) return toast.error("PO number and PO date are required for a formal purchase order");
    if (["TELEPHONIC", "VERBAL"].includes(type) && (!f.reference || !f.confirmedAt)) return toast.error("Customer reference and confirmation date/time are required");
    if (["OTHER", "INTERNAL_EXCEPTION"].includes(type) && f.explanation.trim().length < 5) return toast.error("A short explanation is required");
    if (!quote && !f.amount) return toast.error("Total amount is required when no quotation is linked");
    setBusy(true);
    try {
      await api.post("/vtiger/customer-pos", {
        customer_master_company_id: customerMc, quotation_id: quotationId || null, confirmation_type: type,
        customer_po_number: formal ? f.poNum : null, po_date: formal ? f.poDate : null,
        confirmation_reference: f.reference || null, confirmed_at: formal ? null : (f.confirmedAt ? new Date(f.confirmedAt).toISOString() : null), confirmed_by: f.confirmedBy || null,
        evidence: type === "EMAIL" ? { email_message_id: f.messageId || null } : type === "WHATSAPP" ? { whatsapp_message_id: f.messageId || null } : {},
        explanation: f.explanation || null, total_amount_inr: quote ? null : Number(f.amount), payment_terms: f.terms || null, notes: f.notes || null,
      });
      toast.success("Order confirmation recorded" + (quotationId ? " — quotation accepted, order created" : ""));
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Create failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={onClose} data-testid="new-cpo-drawer">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-xl bg-white h-full overflow-auto p-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-heading font-semibold">Record Order Confirmation</h2>
          <button onClick={onClose} className="text-slate-500 text-[13px]">Close ✕</button>
        </div>
        <div className="mt-4 space-y-3">
          <Field label="Order confirmation type *">
            <select value={type} onChange={(e) => setType(e.target.value)} className={inp} data-testid="new-cpo-type">
              {TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </Field>
          {AUTO_TYPES.includes(type) && <div className="rounded-md border border-sky-200 bg-sky-50 p-2 text-[11px] text-sky-900" data-testid="new-cpo-auto-note">This type is recorded automatically when the customer accepts on the Digital Quote / portal — no manual entry needed. Record it here only for a confirmation that happened outside ONE.</div>}
          <Field label="Linked quotation (recommended)">
            <select value={quotationId} onChange={(e) => setQuotationId(e.target.value)} className={inp} data-testid="new-cpo-quotation">
              <option value="">— None —</option>
              {quotations.map((q) => (<option key={q.id} value={q.id}>{q.code} — {q.subject}</option>))}
            </select>
          </Field>
          <Field label="Customer *">
            <select value={customerMc} onChange={(e) => setCustomerMc(e.target.value)} className={inp} data-testid="new-cpo-customer">
              <option value="">— Select —</option>
              {customers.map((c) => <option key={c.id} value={c.id}>{orgLabel(c, customers)}</option>)}
            </select>
          </Field>
          {formal && <>
            <Field label="PO number *"><Input value={f.poNum} onChange={set("poNum")} data-testid="new-cpo-num" /></Field>
            <Field label="PO date *"><Input type="date" value={f.poDate} onChange={set("poDate")} data-testid="new-cpo-date" /></Field>
            <div className="text-[11px] text-slate-500">Upload the PO PDF from the confirmation after saving — ONE extracts and validates PO number, GSTIN, amount and tax against the quotation.</div>
          </>}
          {!formal && <>
            <Field label={["TELEPHONIC", "VERBAL"].includes(type) ? "Customer reference * (who confirmed / their reference)" : "Confirmation reference"}>
              <Input value={f.reference} onChange={set("reference")} placeholder={type === "EMAIL" ? "email subject or thread" : type === "WHATSAPP" ? "WhatsApp conversation" : "e.g. call with Mr Anand, ref OC-1021"} data-testid="new-cpo-reference" /></Field>
            {["EMAIL", "WHATSAPP"].includes(type) && <Field label={type === "EMAIL" ? "Email message id / link" : "WhatsApp message id / link"}><Input value={f.messageId} onChange={set("messageId")} data-testid="new-cpo-message" /></Field>}
            <Field label={["TELEPHONIC", "VERBAL"].includes(type) ? "Confirmation date / time *" : "Confirmation date / time"}><Input type="datetime-local" value={f.confirmedAt} onChange={set("confirmedAt")} data-testid="new-cpo-confirmed-at" /></Field>
            <Field label="Confirmed by (sender / caller)"><Input value={f.confirmedBy} onChange={set("confirmedBy")} data-testid="new-cpo-confirmed-by" /></Field>
            {["OTHER", "INTERNAL_EXCEPTION"].includes(type) && <Field label="Short explanation *"><Input value={f.explanation} onChange={set("explanation")} data-testid="new-cpo-explanation" /></Field>}
          </>}
          {quote ? (
            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-2 text-[12px]" data-testid="new-cpo-inherited">
              <div className="font-semibold text-emerald-900">Value inherited from {quote.code}</div>
              <div className="text-slate-700">Subtotal {fmtINR(quote.subtotal_inr)} · Tax {fmtINR(quote.tax_inr)} · <b>Total {fmtINR(quote.grand_total_inr || quote.total_inr)}</b>{quote.customer_gstin ? ` · GSTIN ${quote.customer_gstin}` : ""}</div>
              <div className="text-[10px] text-slate-500">Tax is never typed by hand — it comes from the quotation.</div>
            </div>
          ) : (
            <Field label="Total amount (INR) *"><Input type="number" value={f.amount} onChange={set("amount")} data-testid="new-cpo-amount" /></Field>
          )}
          <Field label="Payment terms"><Input value={f.terms} onChange={set("terms")} placeholder={quote?.payment_terms || "e.g. Net 30"} data-testid="new-cpo-terms" /></Field>
          <Field label="Notes"><Textarea rows={2} value={f.notes} onChange={set("notes")} data-testid="new-cpo-notes" /></Field>
        </div>
        <div className="mt-5 flex gap-2">
          <Button variant="outline" onClick={onClose} className="flex-1 h-10">Cancel</Button>
          <Button onClick={submit} disabled={busy} className="flex-1 h-10 bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="new-cpo-submit">
            <CheckCircle2 className="h-4 w-4 mr-1.5" />
            {busy ? "Recording…" : "RECORD ORDER CONFIRMATION"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{label}</div>
      {children}
    </div>
  );
}

function CPODetailDrawer({ id, onClose }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  const [showValidate, setShowValidate] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const reload = useCallback(async () => {
    try {
      const [d, a] = await Promise.all([
        api.get(`/vtiger/customer-pos/${id}`),
        api.get(`/vtiger/customer-pos/${id}/amendment-alerts`)
          .catch(() => ({ data: { items: [] } })),
      ]);
      setData(d.data);
      setAlerts(a.data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed"); onClose();
    } finally { setBusy(false); }
  }, [id, onClose]);
  useEffect(() => { reload(); }, [reload]);

  const cpo = data?.customer_po;
  const validationStatus = cpo?.validation_status;
  const checks = cpo?.validation_checks || [];

  const overallTone = validationStatus === "VALIDATED"
    ? "bg-emerald-50 text-emerald-800 border-emerald-200"
    : validationStatus === "REVIEW REQUIRED"
    ? "bg-amber-50 text-amber-800 border-amber-200"
    : validationStatus === "FAILED"
    ? "bg-rose-50 text-rose-800 border-rose-200"
    : "bg-slate-50 text-slate-700 border-slate-200";

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={onClose}
      data-testid="cpo-detail-drawer">
      <div onClick={(e) => e.stopPropagation()}
        className="w-full max-w-3xl bg-white h-full overflow-auto p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-heading font-semibold">
              {cpo?.code || "…"}
            </h2>
            <div className="text-[12px] text-slate-500 mt-1" data-testid="cpo-detail-source">
              {cpo?.confirmation_type_label || "Formal Purchase Order"}{cpo?.customer_po_number ? ` · PO ${cpo.customer_po_number}` : cpo?.confirmation_reference ? ` · Ref ${cpo.confirmation_reference}` : ""}{cpo?.confirmed_by ? ` · by ${cpo.confirmed_by}` : ""}{cpo?.confirmed_at ? ` · ${new Date(cpo.confirmed_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })} IST` : ""}
            </div>
          </div>
          <button onClick={onClose} className="text-slate-500 text-[13px]">Close ✕</button>
        </div>
        {busy && <div className="mt-8 text-center text-slate-500 text-sm">Loading…</div>}
        {!busy && data && (
          <>
            <div className="mt-5 grid grid-cols-3 gap-3">
              <InfoTile label={cpo.confirmation_type && cpo.confirmation_type !== "FORMAL_PO" ? "Confirmed value (from quotation)" : "PO amount"} value={fmtINR(cpo.total_amount_inr)} bold />
              {data.cost_visible && (
                <>
                  <InfoTile label="Vendor total" value={fmtINR(data.vendor_total_inr)} tone="warn" />
                  <InfoTile label="Gross margin"
                    value={data.gross_margin_inr != null
                      ? `${fmtINR(data.gross_margin_inr)} (${data.margin_pct}%)`
                      : "—"} tone="ok" />
                </>
              )}
            </div>

            {data.linked_quotation && (
              <div className="mt-5">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Linked Quotation</div>
                <div className="border border-slate-200 rounded-md p-3 flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-[13px] font-semibold">{data.linked_quotation.code}</span>
                  <span className="text-[12px] text-slate-700">{data.linked_quotation.subject}</span>
                  <Badge className="text-[10px] border-emerald-200 bg-emerald-50 text-emerald-700">{data.linked_quotation.status}</Badge>
                  {data.linked_quotation.customer_gstin && (
                    <span className="text-[10px] text-slate-500 font-mono ml-auto">
                      GSTIN: {data.linked_quotation.customer_gstin}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* PO Amendment Alerts banner */}
            {alerts.length > 0 && (
              <div className="mt-5 border rounded-md overflow-hidden"
                data-testid="po-amendment-alerts">
                {alerts.map((a) => (
                  <div key={a.id}
                    className={"p-3 flex items-start gap-2 " + (
                      a.acknowledged
                        ? "bg-slate-50 border-slate-200"
                        : a.severity === "HIGH"
                          ? "bg-rose-50 border-rose-200"
                          : "bg-amber-50 border-amber-200"
                    )}
                    data-testid={`po-alert-${a.id}`}>
                    <AlertTriangle className={"h-4 w-4 mt-0.5 " + (
                      a.acknowledged ? "text-slate-500"
                        : a.severity === "HIGH" ? "text-rose-700" : "text-amber-700"
                    )} />
                    <div className="flex-1 text-[12px]">
                      <div className="font-semibold text-slate-900">
                        {a.kind === "PO_TOTAL_HIGHER"
                          ? "Customer PO total is HIGHER than the quotation"
                          : "Customer PO total is LOWER than the quotation"}
                        <Badge className={"ml-2 text-[10px] border " + (
                          a.severity === "HIGH"
                            ? "bg-rose-100 text-rose-800 border-rose-200"
                            : "bg-amber-100 text-amber-800 border-amber-200"
                        )}>{a.severity}</Badge>
                        {a.acknowledged && (
                          <Badge className="ml-2 text-[10px] border bg-emerald-50 text-emerald-800 border-emerald-200">
                            Acknowledged
                          </Badge>
                        )}
                      </div>
                      <div className="text-slate-700 mt-1">{a.detail}</div>
                      <div className="text-[10px] text-slate-500 mt-0.5 font-mono">
                        Quote {fmtINR(a.quote_total_inr)} → PO {fmtINR(a.po_total_inr)}
                        {" · "}
                        Delta {fmtINR(a.delta_inr)} ({a.delta_pct}%)
                        {a.sales_owner_email && ` · Owner ${a.sales_owner_email}`}
                      </div>
                      {a.acknowledge_note && (
                        <div className="text-[11px] text-slate-600 italic mt-1">
                          Ack note: {a.acknowledge_note}
                        </div>
                      )}
                    </div>
                    {!a.acknowledged && (
                      <Button size="sm" variant="outline" onClick={async () => {
                          const note = window.prompt("Acknowledgement note (optional):", "");
                          if (note === null) return;
                          try {
                            await api.post(
                              `/vtiger/po-amendment-alerts/${a.id}/acknowledge`,
                              { note });
                            toast.success("Alert acknowledged");
                            reload();
                          } catch (e) {
                            toast.error(e.response?.data?.detail || "Failed");
                          }
                        }}
                        className="border-slate-400 text-slate-800"
                        data-testid={`po-alert-ack-${a.id}`}>
                        Acknowledge
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* ---------- PO Validation Panel ---------- */}
            <div className="mt-5 border border-slate-200 rounded-md p-4 bg-slate-50/60"
              data-testid="cpo-validation-panel">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                    <ShieldCheck className="h-3 w-3" /> PO Validation
                  </div>
                  <div className="mt-1 flex items-center gap-2">
                    <Badge className={"text-[10px] border " + overallTone}
                      data-testid="cpo-validation-status">
                      {validationStatus || "NOT_VALIDATED"}
                    </Badge>
                    {cpo.validation_at && (
                      <span className="text-[11px] text-slate-500">
                        {new Date(cpo.validation_at).toLocaleString()} · {cpo.validation_by}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  {cpo.attachment_id && (
                    <Button size="sm" variant="outline" onClick={() => {
                      const url = `${process.env.REACT_APP_BACKEND_URL}/api/vtiger/customer-pos/${cpo.id}/pdf`;
                      window.open(url, "_blank");
                    }} data-testid="cpo-view-pdf">
                      <FileText className="h-3.5 w-3.5 mr-1" /> View PDF
                    </Button>
                  )}
                  <Button size="sm" onClick={() => setShowValidate(true)}
                    className="bg-emerald-700 hover:bg-emerald-800 text-white"
                    data-testid="cpo-run-validation">
                    <ShieldCheck className="h-3.5 w-3.5 mr-1" />
                    {checks.length ? "Re-run Validation" : "Attach PO & Validate"}
                  </Button>
                </div>
              </div>
              {checks.length > 0 && (
                <div className="mt-3 space-y-1.5">
                  {checks.map((c, i) => (
                    <div key={i} className="flex items-start gap-2 text-[12px]"
                      data-testid={`cpo-check-${i}`}>
                      {c.status === "PASS" && <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5" />}
                      {c.status === "REVIEW" && <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5" />}
                      {c.status === "FAIL" && <AlertTriangle className="h-4 w-4 text-rose-600 mt-0.5" />}
                      <div className="flex-1">
                        <div className="font-medium text-slate-800">{c.rule}</div>
                        {c.detail && <div className="text-[11px] text-slate-500 font-mono">{c.detail}</div>}
                      </div>
                      <Badge className={"text-[10px] border " + (
                        c.status === "PASS" ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                        : c.status === "REVIEW" ? "bg-amber-50 text-amber-800 border-amber-200"
                        : "bg-rose-50 text-rose-800 border-rose-200"
                      )}>{c.status}</Badge>
                    </div>
                  ))}
                </div>
              )}
              {checks.length === 0 && (
                <div className="mt-3 text-[12px] text-slate-500">
                  Validation not yet run. Upload the customer PO PDF and capture GSTIN + totals to compare against the linked quotation.
                </div>
              )}
            </div>

            {/* ---------- Sales Order Generation ---------- */}
            <div className="mt-5 border border-slate-200 rounded-md p-4 bg-emerald-50/40"
              data-testid="cpo-so-panel">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                    <Truck className="h-3 w-3" /> Sales Order
                  </div>
                  {cpo.sales_order_code ? (
                    <div className="mt-1 flex items-center gap-2 text-[13px]">
                      <span className="font-mono font-semibold">{cpo.sales_order_code}</span>
                      <Badge className="text-[10px] border bg-emerald-50 text-emerald-800 border-emerald-200">
                        Generated
                      </Badge>
                    </div>
                  ) : (
                    <div className="mt-1 text-[12px] text-slate-600">
                      Convert this PO into a numbered Sales Order to begin fulfilment tracking.
                    </div>
                  )}
                </div>
                {!cpo.sales_order_code && (
                  <Button size="sm" onClick={async () => {
                      const canGenerate = validationStatus === "VALIDATED";
                      const forceMode = validationStatus === "REVIEW REQUIRED";
                      if (validationStatus === "FAILED") {
                        toast.error("Cannot generate SO — PO validation FAILED");
                        return;
                      }
                      if (!validationStatus) {
                        toast.error("Run validation first before generating a Sales Order");
                        return;
                      }
                      if (!canGenerate && !forceMode) {
                        toast.error(`Cannot generate: validation is ${validationStatus}`);
                        return;
                      }
                      const msg = forceMode
                        ? "Validation is REVIEW REQUIRED. Generate SO anyway (with force flag)?"
                        : `Generate a numbered Sales Order for ${cpo.code}?`;
                      if (!window.confirm(msg)) return;
                      try {
                        const { data } = await api.post(
                          `/vtiger/customer-pos/${cpo.id}/generate-sales-order`,
                          { force: forceMode });
                        toast.success(`Sales Order ${data.code} generated`);
                        reload();
                      } catch (e) {
                        toast.error(e.response?.data?.detail || "Generation failed");
                      }
                    }}
                    className="bg-emerald-700 hover:bg-emerald-800 text-white"
                    data-testid="cpo-generate-so">
                    <Truck className="h-3.5 w-3.5 mr-1" /> Generate Sales Order
                  </Button>
                )}
                {cpo.sales_order_code && (
                  <Link to="/commercial/crm/sales-orders"
                    className="text-[12px] text-emerald-700 hover:underline flex items-center gap-1">
                    View in Sales Orders <ChevronLeft className="h-3 w-3 rotate-180" />
                  </Link>
                )}
              </div>
            </div>

            {data.cost_visible && (
              <div className="mt-5">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-2">
                  <TrendingUp className="h-3 w-3" /> Linked Vendor POs (margin driver)
                </div>
                <div className="border border-slate-200 rounded-md divide-y">
                  {data.linked_vendor_pos.length === 0 && (
                    <div className="p-4 text-center text-[12px] text-slate-500">
                      No vendor POs linked yet. Link one from the Vendor POs page to see margin.
                    </div>
                  )}
                  {data.linked_vendor_pos.map((v) => (
                    <div key={v.id} className="p-3 flex items-center gap-3 text-[12px]"
                      data-testid={`cpo-linked-vpo-${v.id}`}>
                      <span className="font-mono font-semibold text-slate-900">{v.code}</span>
                      <span className="text-slate-700">{v.vendor_name}</span>
                      <span className="text-slate-500">· {(v.items || []).length} line(s)</span>
                      <span className="ml-auto font-mono font-semibold">{fmtINR(v.grand_total_inr)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {cpo.notes && (
              <div className="mt-5">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Notes</div>
                <div className="text-[13px] text-slate-700 bg-slate-50 rounded p-3">{cpo.notes}</div>
              </div>
            )}
          </>
        )}
        {showValidate && cpo && (
          <ValidateModal cpo={cpo} quote={data?.linked_quotation}
            onClose={() => setShowValidate(false)}
            onDone={() => { setShowValidate(false); reload(); }} />
        )}
      </div>
    </div>
  );
}

function ValidateModal({ cpo, quote, onClose, onDone }) {
  const [poGstin, setPoGstin] = useState(cpo.po_gstin || "");
  const [poSubtotal, setPoSubtotal] = useState(cpo.po_subtotal ?? "");
  const [poTax, setPoTax] = useState(cpo.po_tax ?? "");
  const [poGrandTotal, setPoGrandTotal] = useState(cpo.po_grand_total ?? cpo.total_amount_inr ?? "");
  const [pdfB64, setPdfB64] = useState(null);
  const [pdfName, setPdfName] = useState(null);
  const [busy, setBusy] = useState(false);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 3_000_000) { toast.error("PO PDF must be <= 3 MB"); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const b64 = String(reader.result || "").split(",")[1] || "";
      setPdfB64(b64); setPdfName(f.name);
    };
    reader.readAsDataURL(f);
  };

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        po_gstin: poGstin || null,
        po_subtotal: poSubtotal === "" ? null : Number(poSubtotal),
        po_tax: poTax === "" ? null : Number(poTax),
        po_grand_total: poGrandTotal === "" ? null : Number(poGrandTotal),
        po_pdf_b64: pdfB64 || null,
        po_filename: pdfName || null,
      };
      const { data } = await api.post(`/vtiger/customer-pos/${cpo.id}/validate`, payload);
      toast.success(`Validation: ${data.validation_status}`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Validation failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4"
      onClick={onClose} data-testid="validate-modal">
      <div onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg bg-white rounded-md p-6 shadow-xl overflow-auto max-h-[92vh]">
        <div className="text-[10px] uppercase tracking-[0.14em] text-emerald-700">
          {cpo.code}
        </div>
        <h3 className="text-xl font-heading font-semibold text-slate-900 mt-1">
          Capture &amp; Validate Customer PO
        </h3>
        {quote && (
          <div className="mt-2 text-[11px] text-slate-500">
            Comparing against quotation <span className="font-mono">{quote.code}</span>
            {quote.customer_gstin && <> · Expected GSTIN <span className="font-mono">{quote.customer_gstin}</span></>}
          </div>
        )}
        <div className="mt-4 grid grid-cols-2 gap-3">
          <VField label="PO GSTIN">
            <Input value={poGstin} onChange={(e) => setPoGstin(e.target.value.toUpperCase())}
              placeholder="15 chars" data-testid="validate-gstin" />
          </VField>
          <VField label="PO Grand Total (INR)">
            <Input type="number" value={poGrandTotal}
              onChange={(e) => setPoGrandTotal(e.target.value)}
              data-testid="validate-grand-total" />
          </VField>
          <VField label="PO Subtotal (INR)">
            <Input type="number" value={poSubtotal}
              onChange={(e) => setPoSubtotal(e.target.value)}
              data-testid="validate-subtotal" />
          </VField>
          <VField label="PO Tax (INR)">
            <Input type="number" value={poTax} onChange={(e) => setPoTax(e.target.value)}
              data-testid="validate-tax" />
          </VField>
        </div>
        <div className="mt-3">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
            Customer PO PDF {cpo.attachment_id && "(replacing existing)"}
          </div>
          <label className="border border-dashed border-slate-300 rounded-md p-3 flex items-center gap-2 cursor-pointer hover:bg-slate-50">
            <Upload className="h-4 w-4 text-slate-500" />
            <span className="text-[12px] text-slate-700">
              {pdfName ? pdfName : "Attach PDF (≤ 3 MB)"}
            </span>
            <input type="file" accept="application/pdf" onChange={onFile}
              className="hidden" data-testid="validate-pdf-input" />
          </label>
        </div>
        <div className="mt-5 flex gap-2">
          <Button variant="outline" onClick={onClose} className="flex-1 h-10">Cancel</Button>
          <Button onClick={submit} disabled={busy}
            className="flex-1 h-10 bg-emerald-700 hover:bg-emerald-800 text-white"
            data-testid="validate-submit">
            <ShieldCheck className="h-4 w-4 mr-1.5" />
            {busy ? "Validating…" : "Run Validation"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function VField({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{label}</div>
      {children}
    </div>
  );
}

function InfoTile({ label, value, bold, tone }) {
  const tint = tone === "ok" ? "text-emerald-700"
             : tone === "warn" ? "text-amber-700" : "text-slate-900";
  return (
    <div className="border border-slate-200 rounded-md p-2.5">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-[13px] mt-0.5 ${tint} ${bold ? "font-semibold font-mono" : ""}`}>
        {value}
      </div>
    </div>
  );
}
